import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import {
  useGetAllPoolLocations,
  useCreatePoolLocation,
  useUpdatePoolLocation,
  useDeletePoolLocation,
} from "../../service/api";
import { PoolLocation } from "../../types";

type PoolForm = {
  name: string;
  name_he: string;
};

const emptyForm = (): PoolForm => ({ name: "", name_he: "" });

const cellSx = { backgroundColor: "#fff", color: "#111", borderBottom: "1px solid #f0f0f0" };
const headCellSx = { backgroundColor: "#fafafa", color: "#111", fontWeight: 700, borderBottom: "1px solid #e0e0e0" };

export const PoolsManagement = () => {
  const { t } = useTranslation();
  const { data: pools = [], isLoading } = useGetAllPoolLocations();
  const createPool = useCreatePoolLocation();
  const updatePool = useUpdatePoolLocation();
  const deletePool = useDeletePoolLocation();

  const [editingPool, setEditingPool] = useState<PoolLocation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deletingPool, setDeletingPool] = useState<PoolLocation | null>(null);
  const [form, setForm] = useState<PoolForm>(emptyForm());

  const openCreate = () => {
    setEditingPool(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (pool: PoolLocation) => {
    setEditingPool(pool);
    setForm({ name: pool.name, name_he: pool.name_he ?? "" });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPool(null);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name.trim(),
      name_he: form.name_he.trim() || undefined,
    };

    if (editingPool) {
      await updatePool.mutateAsync({ id: editingPool.id, data: payload });
    } else {
      await createPool.mutateAsync(payload);
    }
    closeForm();
  };

  const handleDelete = async () => {
    if (!deletingPool) return;
    await deletePool.mutateAsync(deletingPool.id);
    setDeletingPool(null);
  };

  const isSaving = createPool.isPending || updatePool.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", backgroundColor: "#fff" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: "#111", mb: 1.5 }}>
          {t("poolsManagement.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, color: "#fff" }}
        >
          Create Pool
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: "1px solid #e0e0e0", overflow: "auto", backgroundColor: "#fff", maxWidth: 600, maxHeight: 480 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Name (EN)</TableCell>
                <TableCell sx={headCellSx}>Name (HE)</TableCell>
                <TableCell sx={{ ...headCellSx, width: 120 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pools.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ ...cellSx, py: 6, color: "#888" }}>
                    No pools yet. Click "Create Pool" to add one.
                  </TableCell>
                </TableRow>
              ) : (
                pools.map((pool) => (
                  <TableRow key={pool.id} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                    <TableCell sx={cellSx}>{pool.name}</TableCell>
                    <TableCell sx={{ ...cellSx, color: pool.name_he ? "#111" : "#bbb" }}>
                      {pool.name_he || "—"}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(pool)} sx={{ color: "#1976d2", mr: 0.5 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeletingPool(pool)} sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={showForm}
        onClose={closeForm}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, backgroundColor: "#fff" } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#111" }}>
          {editingPool ? "Edit Pool" : "Create Pool"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Name (English) *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            autoFocus
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
          />
          <TextField
            label="Name (Hebrew)"
            value={form.name_he}
            onChange={(e) => setForm({ ...form, name_he: e.target.value })}
            fullWidth
            size="small"
            inputProps={{ dir: "rtl" }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.name.trim() || isSaving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isSaving ? "Saving…" : editingPool ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingPool}
        onClose={() => setDeletingPool(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, backgroundColor: "#fff" } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#111" }}>Delete Pool</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#333" }}>
            Are you sure you want to delete <strong>{deletingPool?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeletingPool(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deletePool.isPending}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {deletePool.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
